import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { JobTypeService } from '../../services/job-type.service';
import { DynamicRouteHandlingService } from 'libs/shared/src/lib/services/dynamic-route-handling.service';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { TableModule } from '@openng/optimus-ui/table';
import { NgbDropdownItem, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { SvgIconsComponent } from '@zayan-erp/shared';
import { JobType } from '../../models/job-type.models';
import { ReactiveFormsModule } from '@angular/forms';
import { HeaderService, ConfirmHelperService, HeaderSearchResult } from '@zayan-erp/shared';

@Component({
  selector: 'lib-job-type',
  standalone: true,
  imports: [
    TableModule,
    NgbDropdownModule,
    NgbDropdownItem,
    SvgIconsComponent,
    ReactiveFormsModule,
    RouterOutlet,
    RouterLink
  ],
  templateUrl: './job-type.component.html'
})
export class JobTypeComponent implements OnInit {
  #headerSearchService = inject(HeaderService);
  #destroyRef = inject(DestroyRef);
  ngOnInit(): void {
    this.#headerSearchService.setSearchConfig({
      showSearch: true,
      showIcon: false,
      placeholder: 'Search in Job Types',
      showSearchResults: false,

      search: (query: string) => {
        this.search.set(query);
        return of([]);
      },
      handleResult: (result: HeaderSearchResult) => {}
    });

    // Listen for query parameter changes to refresh data
    this.#activatedRoute.queryParams.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(params => {
      if (params['refresh']) {
        this.jobTypes.reload();
      }
    });
  }
  #activatedRoute = inject(ActivatedRoute);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #confirmHelperService = inject(ConfirmHelperService);
  #jobTypeService = inject(JobTypeService);
  subscription: Subscription | null = null;
  search = signal<string>('');

  #router = inject(Router);
  reload = toSignal(this.#dynamicRouteHandling.reload);

  constructor() {
    this.#dynamicRouteHandling.submitButton.pipe(takeUntilDestroyed()).subscribe(() => {
      this.onSaveJob();
    });
  }
  selectedJobId = signal<string | null>(this.#activatedRoute.snapshot.queryParamMap.get('jobId') ?? null);
  getData = computed(
    () =>
      this.jobTypes
        .value()
        ?.filter(
          (jobType: JobType) =>
            jobType.code.toLowerCase().includes(this.search().toLowerCase()) ||
            jobType.description?.toLowerCase().includes(this.search().toLowerCase())
        ) ?? []
  );

  jobTypes = rxResourceFixed({
    params: () => ({ reload: this.reload() }),
    defaultValue: [],
    stream: () => this.#jobTypeService.readJobTypes()
  });

  handleComponent() {
    this.#router.navigate([`/home-building/job-types/${this.selectedJobId() ? `/${this.selectedJobId()}` : 'add'}`]);
  }

  onAddNew() {
    this.#router.navigate(['/home-building/job-types/add']);
  }
  editJobType(id: string) {
    this.selectedJobId.set(id);
    this.#router.navigate([`/home-building/job-types/${id}`]);
  }

  onSaveJob() {
    this.handleComponent();
  }

  onDeleteJobType(id: string) {
    this.selectedJobId.set(id);
    this.#confirmHelperService
      .showModal({
        icon: 'confirm-icon',
        header: 'Delete Job Type',
        message: 'Are you sure you want to delete this job type?',
        acceptLabel: 'Delete',
        acceptSeverity: 'danger',
        rejectLabel: 'Cancel'
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.#jobTypeService.deleteJobType(this.selectedJobId()!).subscribe({
            next: () => {
              this.selectedJobId.set(null);
              this.jobTypes.reload();
            }
          });
        }
      });
  }
}
